/**
 * Product master contract — S1.2 schema rewrite, rebased onto col J (S0.1).
 *
 * Field content pinned to the 0708 xlsx **col-J** `MoSCoW (R&D)` register
 * (**74** Must, join by label — col K is stale) + the house-board 10-section
 * create skeleton (see `src/config/productFieldRegistry.ts` for the per-field
 * requiredness data — requiredness is DATA, not code).
 *
 * Identity rules:
 * - `internalProductId` (PRD-xxxxxx) is the PRIMARY identity — system-assigned,
 *   never enterable.
 * - `sku` is dup-guarded (409 + `/products/check-sku`).
 * - `netSuiteItemId` is a read-only external mapping (Tier-1 reference).
 * - `scn` is an OPTIONAL retroactive shared-product link group — never set on
 *   create, never gates readiness. required-iff-共用 (DD!19 看SCN狀況): when
 *   `isShared` is true and no SCN is linked, the field registry reports an
 *   ATTENTION state (`getAttentionRules`) — still never a readiness gap.
 *
 * Two-tier readiness (Product Ready 產品就緒):
 * - create-required = Identity + Units + Classification exactly.
 * - Ready = create set + 3 descriptions (incl. 報關品名) + basePrice +
 *   costingMethod. Computed on READ (`ProductReadiness`) — never stored, never
 *   a save-blocker. Only Ready products may enter SO/PO.
 */

import type { ProductSalesStats } from '@/lib/reports/salesHistorySelectors';

// ---------------------------------------------------------------------------
// Readiness — computed on read (replaces the codex computeOccStatus model)
// ---------------------------------------------------------------------------

import type { PricingAnchor } from './pricing';

/** Tier-2 gap groups that keep a product out of 產品就緒 Product Ready. */
export type ProductReadinessGap = 'descriptions' | 'pricingRef' | 'costingRef';

export interface ProductReadiness {
  ready: boolean;
  missing: ProductReadinessGap[];
}

// ---------------------------------------------------------------------------
// Value objects
// ---------------------------------------------------------------------------

/** Display-only in MVP (Cost Basis defaults OPEN with Judy/林芊 — Appendix B). */
export type ProductCostingMethod = 'FIFO' | 'MOVING_AVERAGE' | 'STANDARD' | 'WEIGHTED_AVERAGE';

/**
 * Item Weight Unit (0708-J Must) — entry/display qualifier for the weight
 * value. The field NAME `weightKg` is kept for compat; its value is expressed
 * in `weightUnit` (default 'kg').
 */
export type ProductWeightUnit = 'kg' | 'g' | 'lb';

export const PRODUCT_WEIGHT_UNITS: readonly ProductWeightUnit[] = ['kg', 'g', 'lb'];

/** 商品備註 visibility audience (US-183). */
export type ProductNoteAudience = 'all' | 'procurement' | 'sales' | 'finance';

/**
 * 商品備註 (US-183 Must) — per-product multi-line notes, each with a
 * visibility audience. Schema + CRUD only in this wave; the
 * operation-triggered POP-UP behaviour is explicitly OUT of scope (owner:
 * Sherl — which operations count is undecided).
 */
export interface ProductNote {
  id: string;
  text: string;
  audience: ProductNoteAudience;
  createdAt: string;
}

export interface ProductCustomerPartNumber {
  id: string;
  customerId: number;
  customerName: string;
  partNumber: string;
  description?: string;
  /**
   * Dormant Customer Item Authorization columns — explicitly NOT-in-build
   * (no UI); shipped so the schema does not churn when the decision lands.
   */
  isAuthorized?: boolean;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}

/**
 * 備件料號 (2026-08-27 session §5) — the product's OWN spare-part alias in the
 * D8 external-identifier posture (maps to Internal Product ID). Deliberately
 * carries NO customer/vendor key: keying it on a party would be a NEW
 * decision, not a mirror of 客戶料號.
 */
export interface ProductSparePartNumber {
  id: string;
  partNumber: string;
  description?: string;
}

/**
 * Multi-vendor sourcing line. Invariant: whenever a product has any vendor
 * lines, EXACTLY ONE carries `isPrimary` (enforced by the mock handler's
 * `applyPrimaryVendorLine`; asserted by vitest).
 */
export interface ProductVendorLine {
  id: string;
  productId: string;
  vendorId: number;
  vendorName: string;
  /** Vendor part number (prints on vendor PO doc endpoint). */
  vendorSku?: string;
  /** Real negotiated unit cost (feeds PO prefill + costing). */
  unitCost: number;
  currency: string;
  /**
   * 訂購單位 (W7-B3, 2026-08-27 session §2) — the unit this vendor sells in,
   * from the product's own UNIT_VALUES family. CAPTURE/DISPLAY ONLY: no
   * quantity conversion against stockUnit exists anywhere (conversion math is
   * decision-gated — Q5, Melissa + Ever). Blank when the vendor never named one.
   */
  orderUnit?: string;
  isPrimary: boolean;
  /** @deprecated mirror of `isPrimary` (kept in sync) — legacy consumers only; dies with the S2/S3 handler rewrites. */
  isPreferred: boolean;
  leadTimeDays?: number;
  /**
   * TEGO H51 price pair (US-285) — the primary vendor carries 上次採購價 /
   * 下次採購價. Normally EQUAL; they diverge only across a price change (a
   * quote edits `next`); a goods-receipt post rolls `last := transacted price`;
   * switching the primary vendor sets `last` = the outgoing vendor's price and
   * `next` = the incoming vendor's next-PO price. See `applyVendorPriceChange`
   * (mock handler) for the pure mechanic. Optional/nullable — blank on lines
   * with no purchase history.
   */
  lastPurchasePrice?: number | null;
  /** 下次採購價 (H51) — the price the NEXT PO to this vendor prefills with. */
  nextPurchasePrice?: number | null;
  /**
   * 最低訂購量 MOQ (US-285) — procurement-editable per vendor line. Advisory
   * only at PO create (未達最低訂購量 hint, never a submit block). Nullable
   * when the vendor sets no minimum.
   */
  moq?: number | null;
  /**
   * Per-line 生效/停用 status (US-148 Must) — `undefined` means active
   * (default true). Inactive lines stay visible with a status chip (data
   * status display; hide-don't-disable does not apply here).
   */
  isActive?: boolean;
}

/** PATCH /products/{id}/vendors/{lineId} — per-line status/detail update. */
export interface UpdateProductVendorLineRequest {
  isActive?: boolean;
  /** Negotiated unit cost update (procurement-editable). */
  unitCost?: number;
  /** Purchase lead time (days) update. */
  leadTimeDays?: number;
  /** H51 上次採購價 (normally set by the GR-post roll, but editable). */
  lastPurchasePrice?: number | null;
  /** H51 下次採購價 — a price-change quote edits this field (US-285). */
  nextPurchasePrice?: number | null;
  /** 最低訂購量 MOQ update (US-285). */
  moq?: number | null;
}

export interface ProductFitmentYearRange {
  yearFrom: number;
  yearTo?: number;
  /** Trim/body variant note, e.g. "GUN126R dual cab". */
  variant?: string;
}

/** Make/Model are BOTH multi-select (xlsx sticky R26). */
export interface ProductFitment {
  makes: string[];
  models: string[];
  years: ProductFitmentYearRange[];
}

/** Slotted product image — max 5, slot order is meaningful (TEGO K13). */
export interface ProductImageSlot {
  slot: 1 | 2 | 3 | 4 | 5;
  url: string;
  caption?: string;
}

export const MAX_PRODUCT_IMAGES = 5;

/**
 * Account references + Tax Schedule ship as day-one PLACEHOLDERS
 * (sticky R217) — GL wiring deferred. Intercompany accounts and Tax Schedule
 * are Blocked-CLARIFY (purple `BlockedFieldCallout`, non-editable, no guess).
 *
 * 0708-J alignment: Customer/Vendor Return Variance and Dropship Expense are
 * the three J-Must refs the codex-era schema missed; the deferredRevenue and
 * vendorBill*Variance quartet is codex-era (in neither Must set) and kept only
 * so stored data does not churn.
 */
export interface ProductAccountingRefs {
  incomeAccountId?: string | null;
  cogsAccountId?: string | null;
  assetAccountId?: string | null;
  gainLossAccountId?: string | null;
  priceVarianceAccountId?: string | null;
  quantityVarianceAccountId?: string | null;
  exchangeRateVarianceAccountId?: string | null;
  /** Customer Return Variance (0708-J Must, R217 placeholder). */
  customerReturnVarianceAccountId?: string | null;
  /** Vendor Return Variance (0708-J Must, R217 placeholder). */
  vendorReturnVarianceAccountId?: string | null;
  /** Dropship Expense (0708-J Must, R217 placeholder). */
  dropshipExpenseAccountId?: string | null;
  /** @deprecated codex-era — in neither Must set (0708-J); kept so stored data does not churn. */
  deferredRevenueAccountId?: string | null;
  intercompanyIncomeAccountId?: string | null;
  intercompanyCogsAccountId?: string | null;
  /** @deprecated codex-era — in neither Must set (0708-J); kept so stored data does not churn. */
  vendorBillPriceVarianceAccountId?: string | null;
  /** @deprecated codex-era — in neither Must set (0708-J); kept so stored data does not churn. */
  vendorBillQuantityVarianceAccountId?: string | null;
  /** @deprecated codex-era — in neither Must set (0708-J); kept so stored data does not churn. */
  vendorBillExchangeRateVarianceAccountId?: string | null;
  /** Blocked-CLARIFY pre-override (house board field-state contract). */
  taxScheduleId?: string | null;
}

/**
 * Replenishment planning block (SR0.2 — DD!53–67, J = SHOULD).
 *
 * Tier-2 demand-driven purchasing config. EVERY field is optional/nullable and
 * is NEVER create-required and NEVER readiness-gating (Fixed decision 1) — a
 * product reaches 產品就緒 with the whole block blank. The v1 reorder-suggestion
 * engine (WR1) reads `reorderPoint` / `preferredStockLevel` / `reorderMultiple`
 * / `doNotOrder` / `preOrder`; the rest are registry data + planner inputs.
 *
 * The `*Auto` booleans (and `preferredStockLevelDays`) are DORMANT — stored so
 * the schema does not churn, but rendered NOWHERE until the v2 auto-calc ladder
 * rung (hide-don't-disable). Transfer Price(+Unit) DD!64–65 is routed OUT to
 * the pricing module; per-location overrides DD!80–89 are deferred (OQ-11).
 */
export interface ProductReplenishmentPlanning {
  /** 再訂購點 (Reorder Point) — inventory-position trigger for the engine. */
  reorderPoint?: number | null;
  /** DORMANT (v2) — auto-calculate the reorder point; stored, rendered nowhere. */
  reorderPointAuto?: boolean | null;
  /** 目標水位 (Preferred Stock Level) — top-up target the suggestion fills to. */
  preferredStockLevel?: number | null;
  /** DORMANT (v2) — express the target as days of demand. */
  preferredStockLevelDays?: number | null;
  /** DORMANT (v2) — auto-calculate the target level. */
  preferredStockLevelAuto?: boolean | null;
  /** 補貨倍數 (Reorder Multiple) — order-quantity rounding increment. */
  reorderMultiple?: number | null;
  /** 採購前置期 (Purchase Lead Time, days) — feeds the 應下單日 math. */
  purchaseLeadTimeDays?: number | null;
  /** DORMANT (v2) — auto-calculate lead time from observed PO→GR gaps. */
  purchaseLeadTimeAuto?: boolean | null;
  /** 安全備貨天數 (Safety Stock Days) — buffer days feeding the safety target. */
  safetyStockDays?: number | null;
  /** 季節性需求 (Seasonal Demand) — flag only in v1; the window math is v2. */
  seasonalDemand?: boolean | null;
  /** 預期需求變化 (Expected Demand Change, %) — planner's expected delta. */
  expectedDemandChangePct?: number | null;
  /** 禁止採購 (Do Not Order) — hard-excludes the product from suggestions. */
  doNotOrder?: boolean | null;
  /** 預購 (Pre Order) — allows ordering ahead of availability. */
  preOrder?: boolean | null;
}

// ---------------------------------------------------------------------------
// Legacy compat — deprecated codex-era shapes kept ONLY so concurrently
// rebuilt consumers (ProductListPage/FormPage/DetailPage, useProducts,
// salesOrder/purchaseOrder handlers, legacy taxonomy seed) keep compiling.
// The S1.3/S1.4/S2.1 rebuilds delete their last usages; then this block dies.
// ---------------------------------------------------------------------------

/** @deprecated S0.4 legacy 3-level taxonomy — superseded by `productCategory.ts`; dies with the legacy seed. */
export type TaxonomyLevel = 'class' | 'productGroup' | 'productSubGroup';

/** @deprecated S0.4 legacy 3-level taxonomy — superseded by `ProductCategory`; dies with the legacy seed. */
export interface ProductTaxonomyNode {
  id: string;
  code: string;
  name: string;
  nameZh?: string;
  level: TaxonomyLevel;
  parentId: string | null;
  isActive: boolean;
  children?: ProductTaxonomyNode[];
}

/** @deprecated S1.2 — replaced by `ProductReadiness`; kept for the codex list/detail pages until S1.4. */
export type OccReadinessStatus = 'ready' | 'blocked' | 'draft';

// ---------------------------------------------------------------------------
// Product
// ---------------------------------------------------------------------------

export interface Product {
  id: string;
  /** PRIMARY identity — PRD-xxxxxx, system-assigned on create, never enterable. */
  internalProductId: string;
  /** Optional shared cross-company link group; retroactively assignable, never on create. */
  scn?: string | null;
  /**
   * 共用 flag (DD!19 看SCN狀況) — marks the product as shared across
   * companies and drives the registry's required-iff-共用 attention state on
   * `scn` (shared + unlinked ⇒ attention; never gates 產品就緒). Optional
   * groundwork — absent means not shared. Seeded by S1.1.
   */
  isShared?: boolean;
  /** Dup-guarded (case-insensitive) — 409 on conflict; live check via /products/check-sku. */
  sku: string;
  /** Prints on ALL doc endpoints (報關 / customer docs / vendor PO). */
  displayName: string;

  // -- Classification 分類 (create-required) --------------------------------
  subsidiaryId: string;
  includeChildren?: boolean;
  /** Single primary FK into the 5-capable category tree (leaf-preferred, soft-warn non-leaf). */
  categoryId: string;
  /** Schema-only until a later wave (no UI). */
  secondaryCategoryIds?: string[];
  /** Derived bilingual breadcrumb — computed on read, never stored. */
  categoryPath?: string;

  // -- Units 單位 (create-required, default PCS — sticky R8) ----------------
  unitsTypeId: string;
  stockUnit: string;
  purchaseUnit: string;
  saleUnit: string;

  // -- Descriptions 描述 (Tier-2: all three gate 產品就緒) -------------------
  /** 銷售描述 類別A — customer docs (INVOICE + PACKING). */
  salesDescription?: string;
  /** 採購描述 類別T — vendor PO. */
  purchaseDescription?: string;
  /** 報關品名 — customs doc endpoint. */
  customsDescription?: string;
  /**
   * 採購單備註 DD!46 (0708-J Should, Y-S3) — buying note that flows onto the
   * vendor-PO doc endpoint like `purchaseDescription`; NEVER gates 產品就緒.
   */
  purchaseOrderNote?: string;

  // -- Notes 備註 (0708-J alignment — never gate 產品就緒) -------------------
  /** 商品備註 (US-183 Must) — audience-scoped note list; pop-up behaviour deferred. */
  productNotes?: ProductNote[];
  /** 瑕疵備註 (0708-J Must). */
  damageStockNotes?: string;
  /** 商品註解 (0708-J Must). */
  itemComment?: string;

  // -- External identifiers -------------------------------------------------
  /** Read-only NetSuite item mapping (Tier-1 reference). */
  netSuiteItemId?: string | null;
  netSuiteSyncedAt?: string | null;
  oemNo?: string | null;
  mpn?: string | null;
  upcCode?: string | null;

  // -- Relations ------------------------------------------------------------
  customerPartNumbers?: ProductCustomerPartNumber[];
  /** 備件料號 — party-less spare-part aliases (sibling of 客戶料號). */
  sparePartNumbers?: ProductSparePartNumber[];
  vendorLines?: ProductVendorLine[];
  /** Max 5, slotted (TEGO K13). */
  images?: ProductImageSlot[];

  // -- Fitment 車型適配 + manufacturing -------------------------------------
  fitment?: ProductFitment;
  /**
   * DD!31 Variant 版本/車型代別 (0708-J Must) — product-level Car Details
   * variant; distinct from the per-year-range trim note
   * `ProductFitmentYearRange.variant` (Q-T3).
   */
  variant?: string;
  /** DD!32 Extract Year 拆解年份 / 年份索引 (0708-J Must) — C108 age family. */
  extractYear?: number;
  /** 製造商 (0708-J Must) — distinct from `mpn` / `countryOfManufacture` and from vendor sourcing lines. */
  manufacturer?: string;
  countryOfManufacture?: string;
  /** DD!69 FA Sub Category FA子分類 (0708-J Must). */
  faSubCategory?: string;

  // -- Attributes 屬性 -------------------------------------------------------
  /**
   * Age Group (0708-J Must) — C108 inline 【MUST】: SYSTEM-COMPUTED from
   * `ageStartYear`/`ageEndYear` (pure `computeAgeGroup`, S1.1); no longer
   * directly enterable.
   */
  ageGroup?: string;
  /** C108 — user-entered vehicle start year; drives the computed `ageGroup`. */
  ageStartYear?: number;
  /** C108 — user-entered vehicle end year; drives the computed `ageGroup`. */
  ageEndYear?: number;

  // -- Physical (M6 volumetric feed) ----------------------------------------
  lengthMm?: number;
  widthMm?: number;
  heightMm?: number;
  /** Weight value expressed in `weightUnit` — field name kept for compat (default kg). */
  weightKg?: number;
  /** Item Weight Unit (0708-J Must) — entry/display qualifier, default 'kg'. */
  weightUnit?: ProductWeightUnit;
  /** Derived from L/W/H — computed on read. */
  cubicFeet?: number;

  // -- Commercial -----------------------------------------------------------
  /**
   * D17 per-product Pricing Anchor (Melissa 2026-08-28: "each product can
   * select" / "should be selective"). null/absent = follow the company
   * default from Price Setup. Config-only — the resolver does not read it.
   */
  pricingAnchor?: PricingAnchor | null;
  costingMethod?: ProductCostingMethod | null;
  /**
   * W6 lock display (2026-08-27): true once the product has transaction
   * records — the costing method renders read-only (state at rest, reason on
   * hover). MOCK-SEEDED predicate only; the real server-side predicate is
   * N9 `costingMethodLocked` (Joe). Absent ⇒ editable as before.
   */
  costingMethodLocked?: boolean;
  basePrice?: number | null;
  currency?: string;
  minQty?: number;
  /** DD!100 Minimum Quantity Unit (0708-J Must) — unit qualifier of `minQty`. */
  minQtyUnit?: string;
  maxQty?: number;
  /** DD!102 Maximum Quantity Unit (0708-J Must) — unit qualifier of `maxQty`. */
  maxQtyUnit?: string;
  itemDefinedCost?: number;
  /** DD!98 Item Defined Cost Unit (0708-J Must) — unit qualifier of `itemDefinedCost` (`weightUnit` pattern). */
  itemDefinedCostUnit?: string;

  // -- Accounting placeholders (R217) ---------------------------------------
  accounting?: ProductAccountingRefs;

  // -- Replenishment planning (SR0.2 — DD!53–67, SHOULD-tier, never gates) ---
  planning?: ProductReplenishmentPlanning;

  isActive: boolean;
  /** Computed on READ (never stored) — present on every API response. */
  readiness?: ProductReadiness;
  createdAt: string;
  updatedAt: string;

  // -- Legacy compat mirrors (deprecated — recomputed on read by the mock
  //    handler; die with the S1.3/S1.4/S2.1 consumer rebuilds) ---------------
  /** @deprecated mirror of `displayName`. */
  name: string;
  /** @deprecated stored zh display label (search demo aid). */
  nameZh?: string;
  /** @deprecated mirror of `salesDescription`. */
  customerDescription?: string;
  /** @deprecated mirror of `purchaseDescription`. */
  vendorDescription?: string;
  /** @deprecated mirror of `oemNo`. */
  oemPartNumber?: string | null;
  /** @deprecated mirror of `mpn`. */
  manufacturerPartNumber?: string | null;
  /** @deprecated mirror of `stockUnit`. */
  baseUom: string;
  /** @deprecated mirror of `purchaseUnit`. */
  purchaseUom?: string;
  /** @deprecated mirror of `saleUnit`. */
  salesUom?: string;
  /** @deprecated dies with the codex form. */
  purchaseToBaseFactor?: number;
  /** @deprecated dies with the codex form. */
  salesToBaseFactor?: number;
  /** @deprecated mirror of `lengthMm` (cm). */
  lengthCm?: number;
  /** @deprecated mirror of `widthMm` (cm). */
  widthCm?: number;
  /** @deprecated mirror of `heightMm` (cm). */
  heightCm?: number;
  /** @deprecated mirror of `weightKg`. */
  grossWeightKg?: number;
  /** @deprecated m³ mirror of the mm dims. */
  cbm?: number;
  /** @deprecated mirror of the primary vendor line. */
  preferredVendorId?: number | null;
  /** @deprecated mirror of the primary vendor line. */
  preferredVendorName?: string | null;
  /** @deprecated mirror of `basePrice`. */
  listPrice?: number;
  /** @deprecated mirror of `readiness` — 'ready' when Ready, else 'blocked'. */
  occStatus: OccReadinessStatus;
  /** @deprecated mirror of `readiness.missing`. */
  occBlockedFields?: string[];
}

// ---------------------------------------------------------------------------
// List / detail contracts
// ---------------------------------------------------------------------------

export interface ProductListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  /** Primary category filter — the handler includes the whole subtree. */
  categoryId?: string;
  readiness?: 'ready' | 'not_ready';
  hasScn?: boolean;
  isActive?: boolean;
  /**
   * @deprecated S1.2 — use `readiness: 'ready'`; still sent by the codex
   * SalesOrderListPage Ready-product picker until the S2.2 rebuild.
   */
  occReadyOnly?: boolean;
}

export interface ProductListResponse {
  data: Product[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface ProductDetailResponse {
  data: Product;
}

// ---------------------------------------------------------------------------
// SKU dup-guard (/products/check-sku)
// ---------------------------------------------------------------------------

export interface CheckSkuParams {
  sku: string;
  /** Exclude one product (edit mode). */
  excludeId?: string;
}

export interface CheckSkuResponse {
  available: boolean;
  conflict?: {
    id: string;
    internalProductId: string;
    sku: string;
    displayName: string;
    isActive: boolean;
  };
}

// ---------------------------------------------------------------------------
// OEM dup-detection (/products/check-oem) — ADVISORY, never a gate
// ---------------------------------------------------------------------------
//
// The shape lived in THREE places (packages/domain `CheckOemMatch`, the MSW
// handler, and `ProductFormWizard.tsx`'s `OemDupMatch`), pinned together by a
// vitest instead of by the type system. Both the domain rule and the wizard
// carry the same recorded follow-up — "hoist into src/types/api/product.ts +
// productApi.checkOem once that file is free". This is that hoist; the wizard's
// exported names are now aliases of these.

/** One same-大類 OEM match. */
export interface CheckOemMatch {
  id: string;
  internalProductId: string;
  sku: string;
  displayName: string;
  subsidiaryId: string;
  oemNo: string;
  scn: string | null;
  /** Board scope notes: same 國家/市場 ⇒ 100% 相同商品; cross-market ⇒ 99%/1%. */
  sameMarket: boolean;
}

export interface CheckOemParams {
  oemNo: string;
  /** Board criterion 1 of 2 — no category, no check. */
  categoryId?: string;
  /** Decides `sameMarket` only; never narrows the search. */
  subsidiaryId?: string;
  /** Exclude one product (edit mode). */
  excludeId?: string;
}

export interface CheckOemResponse {
  matches: CheckOemMatch[];
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Create — required = Identity + Units + Classification exactly (units default
 * to PCS when omitted). Everything else is Tier-2/optional; `scn`,
 * `internalProductId` and qty/ops fields are NEVER on create.
 */
export interface CreateProductRequest {
  sku: string;
  displayName: string;
  subsidiaryId: string;
  categoryId: string;
  includeChildren?: boolean;
  unitsTypeId?: string;
  stockUnit?: string;
  purchaseUnit?: string;
  saleUnit?: string;
  secondaryCategoryIds?: string[];
  salesDescription?: string;
  purchaseDescription?: string;
  customsDescription?: string;
  /** DD!46 採購單備註 (0708-J Should, Y-S3). */
  purchaseOrderNote?: string;
  productNotes?: ProductNote[];
  damageStockNotes?: string;
  itemComment?: string;
  netSuiteItemId?: string | null;
  oemNo?: string | null;
  mpn?: string | null;
  upcCode?: string | null;
  customerPartNumbers?: ProductCustomerPartNumber[];
  /** 備件料號 — party-less spare-part aliases (sibling of 客戶料號). */
  sparePartNumbers?: ProductSparePartNumber[];
  images?: ProductImageSlot[];
  fitment?: ProductFitment;
  /** DD!31 (0708-J Must). */
  variant?: string;
  /** DD!32 (0708-J Must). */
  extractYear?: number;
  manufacturer?: string;
  countryOfManufacture?: string;
  /** DD!69 (0708-J Must). */
  faSubCategory?: string;
  /**
   * C108: system-computed from `ageStartYear`/`ageEndYear` from S1.1 on —
   * retained for compat only; handlers recompute and ignore a sent value.
   */
  ageGroup?: string;
  /** C108 — drives the computed `ageGroup`. */
  ageStartYear?: number;
  /** C108 — drives the computed `ageGroup`. */
  ageEndYear?: number;
  /** 共用 flag (DD!19) — drives required-iff-共用 on `scn`. */
  isShared?: boolean;
  lengthMm?: number;
  widthMm?: number;
  heightMm?: number;
  weightKg?: number;
  weightUnit?: ProductWeightUnit;
  /** D17 per-product anchor — null clears back to follow the company default. */
  pricingAnchor?: PricingAnchor | null;
  costingMethod?: ProductCostingMethod | null;
  basePrice?: number | null;
  currency?: string;
  minQty?: number | null;
  /** DD!100 (0708-J Must) — unit qualifier of `minQty`. */
  minQtyUnit?: string | null;
  maxQty?: number | null;
  /** DD!102 (0708-J Must) — unit qualifier of `maxQty`. */
  maxQtyUnit?: string | null;
  itemDefinedCost?: number | null;
  /** DD!98 (0708-J Must) — unit qualifier of `itemDefinedCost`. */
  itemDefinedCostUnit?: string | null;
  accounting?: ProductAccountingRefs;
  /** SHOULD-tier replenishment planning (SR0.2) — optional, never gates. */
  planning?: ProductReplenishmentPlanning;
  nameZh?: string;
}

/**
 * The create-only fields — set once, never patchable.
 *
 * NOT A PREFERENCE, A READ OF THE CONTRACT. `ProductUpdateInput`
 * (packages/contracts/schema.graphql) declares neither, and the server's
 * `productUpdate` patches neither (`mutablePatch`,
 * server/src/modules/product/graph.ts, maps no `units_type_id` and no
 * `subsidiary_id`). Two independent sources agree, so a PATCH carrying them
 * is not an under-served write — it is a write the contract says cannot exist.
 */
export type ProductCreateOnlyField = 'subsidiaryId' | 'unitsTypeId';

/**
 * PATCH — the create set MINUS the create-only fields, plus the retroactive
 * SCN link.
 *
 * THIS TYPE IS THE COMPILE-TIME HALF OF THE LOST-WRITE GUARD, and it has now
 * been wrong twice in the same way.
 *
 * NO `isActive` (D-3, 2026-08-10). It used to be declared here, and it was
 * the only reason `update({ isActive: true })` type-checked at two call
 * sites — the wire never carried it, so the type was the one thing telling
 * callers the write was possible. Activation is `productApi.setActive`.
 *
 * NO `subsidiaryId` / `unitsTypeId` (D-4 + D-5, 2026-08-10). `extends
 * Partial<CreateProductRequest>` re-opened the identical hole for two more
 * fields the moment D-3 closed it for one, because the create set is NOT the
 * mutable set. Both were MEASURED on the live seam, driving the wizard's own
 * `toUpdateProductRequest(hydrateFromProduct(product))` body:
 *   · `unitsTypeId` reached the wire and `ProductUpdateInput` does not declare
 *     it, so variable coercion refused the WHOLE mutation — HTTP 400, and
 *     every field the user really did edit died with it.
 *   · `subsidiaryId` never reached the wire (the update literal forwards it
 *     only on create), so the save answered 200 over an unmoved row — D-3's
 *     silent class exactly, on an editable two-option Select.
 * Banked: `Docs/senditship-run-2026-08-08/cu1b-d4-d5-immutable-pair-2026-08-10.txt`.
 */
export interface UpdateProductRequest
  extends Omit<Partial<CreateProductRequest>, ProductCreateOnlyField> {
  /**
   * Retroactive SCN link/unlink (`null` unlinks) — SCN is NEVER on create
   * (fixed decision); the ScnLinkDrawer PATCHes it after the fact.
   */
  scn?: string | null;
}

export interface SetPreferredVendorRequest {
  vendorId: number;
}

/**
 * Response envelope for `GET /products/:id/sales-stats` (SR2.3 handler).
 *
 * Lives here rather than in the hook because the read is served through
 * `productApi.salesStats` — a NAMED HOLE (register §0g), not a facade bypass.
 * `asOf` is echoed by the handler so the horizon the trailing windows were
 * measured on travels with the payload.
 */
export interface ProductSalesStatsResponse {
  data: ProductSalesStats;
  asOf: string;
}
